/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  CellRange,
  Column,
  ColumnApi,
  GridApi,
  ICellEditorParams,
  RowNode,
} from '@ag-grid-community/core'
import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'
import {
  allowUpdateBasedOnType,
  isSingleValueCase,
  toISOFormatFromAnyValidDateString,
} from '../../util/common-utils'

@Injectable()
export class PasteService {
  grid!: GridApi
  columnApi!: ColumnApi
  isEdit = false

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  readonly pasteBinding = this.pasteHandler.bind(this)

  pasteHandler(event: ClipboardEvent): boolean {
    const data =
      event.clipboardData
        ?.getData('text/plain')
        .replace(/^\s+|\s+$|\r/g, '')
        .replace(/\r\n/g, '\n') ?? ''
    const rowWiseData = data.split('\n')

    const ranges = this.grid.getCellRanges()
    if (!ranges || ranges.length == 0) {
      return false
    }
    if (ranges.length > 1 && !isSingleValueCase(rowWiseData)) {
      return false
    }

    if (isSingleValueCase(rowWiseData)) {
      this.singleValueMultiRangePaste(rowWiseData[0].split('\t')[0], ranges)
    } else {
      const { startRow, endRow, columns } = ranges[0]
      const column = columns[0]
      const startColIndex = this.getColumnIndexByColId(column.getColId())
      const rowStartIndex = Math.min(
        startRow?.rowIndex ?? 0,
        endRow?.rowIndex ?? 0,
      )
      this.multiValueContinuousPaste(rowWiseData, rowStartIndex, startColIndex)
    }
    return true
  }

  registerGrid(grid: GridApi, columnApi: ColumnApi) {
    this.grid = grid
    this.columnApi = columnApi
    this.document.addEventListener('paste', this.pasteBinding)
  }

  destroy() {
    this.document.removeEventListener('paste', this.pasteBinding)
  }

  multiValueContinuousPaste(
    rowWiseData: string[],
    startRowIndex: number,
    startColumnIndex: number,
  ) {
    const numberOfRows = this.grid.getDisplayedRowCount()
    const rowNodes = this.grid.getRenderedNodes()
    let lastRowId = Number(rowNodes[rowNodes.length - 1].data.rowId)
    let rowEnd = startRowIndex + rowWiseData.length - 1
    if (this.isEdit && rowEnd > numberOfRows - 1) {
      rowEnd = numberOfRows - 1
    }
    const allCols = this.columnApi.getAllDisplayedColumns()
    const columnPastedSet = new Set<string>()
    let rowsCreatedCount = 0
    let rowsPastedCount = 0
    const rowPastedData: unknown[] = []
    for (let i = startRowIndex; i <= rowEnd; i++) {
      rowsPastedCount++
      const row = this.grid.getDisplayedRowAtIndex(i)
      let rowData = row?.data
      if (!row) {
        rowData = { ...this.grid.getDisplayedRowAtIndex(0)?.data }
        Object.keys(rowData).forEach(index => (rowData[index] = ''))
        rowData.rowId = (lastRowId + 1).toString()
        rowData.validityStatus = 'inactive'
        rowsCreatedCount++
        lastRowId++
      }

      const currentRowData = rowWiseData[i - startRowIndex]
      const allCellData = currentRowData.split('\t')
      for (
        let j = startColumnIndex;
        j < startColumnIndex + allCellData.length;
        j++
      ) {
        const currentColumn = allCols ? allCols[j] : undefined
        if (
          currentColumn?.isCellEditable({ data: rowData } as RowNode) ||
          currentColumn?.getColId() == 'mvtBasisSelectionText'
        ) {
          const dataToPaste = this.pasteBasedOnType(
            allCellData[j - startColumnIndex],
            currentColumn,
            rowData,
          )
          if (dataToPaste) {
            rowData[currentColumn.getColId()] = dataToPaste
            columnPastedSet.add(currentColumn.getColId())
          }
        }
      }
      if (!row) {
        if (i == rowEnd) {
          this.grid.applyTransactionAsync({ add: [rowData] }, () => {
            const pasteDoneEvent = new CustomEvent('pasteDone', {
              detail: {
                rowsPastedCount: rowsPastedCount,
                pastedRowData: rowPastedData,
                colPastedSet: columnPastedSet,
              },
            })
            this.document.dispatchEvent(pasteDoneEvent)
          })
        } else {
          this.grid.applyTransactionAsync({ add: [rowData] })
        }
      } else {
        row.setData(rowData)
      }
      rowPastedData.push(rowData)
    }
    if (!rowsCreatedCount) {
      const pasteDoneEvent = new CustomEvent('pasteDone', {
        detail: {
          rowsPastedCount: rowsPastedCount,
          pastedRowData: rowPastedData,
          colPastedSet: columnPastedSet,
        },
      })
      this.document.dispatchEvent(pasteDoneEvent)
    }
  }

  singleValueMultiRangePaste(valueToPaste: string, ranges: CellRange[]) {
    const rowsPastedSet = new Set<RowNode>()
    const columnPastedSet = new Set<string>()
    ranges.forEach(range => {
      let startRow = range.startRow?.rowIndex ?? 0
      let endRow = range.endRow?.rowIndex ?? 0
      // Swap indices if selection was made in reverse order (bottom-to-top)
      if (startRow > endRow) [startRow, endRow] = [endRow, startRow]
      for (let i = startRow; i <= endRow; i++) {
        const rowNodeEffected = this.grid.getDisplayedRowAtIndex(i)
        if (rowNodeEffected) {
          range.columns.forEach(column => {
            if (
              column.isCellEditable(rowNodeEffected) ||
              column.getColId() == 'mvtBasisSelectionText'
            ) {
              const data = this.pasteBasedOnType(
                valueToPaste,
                column,
                rowNodeEffected.data,
              )
              if (data) {
                rowNodeEffected.setDataValue(column, data)
                rowsPastedSet.add(rowNodeEffected)
                columnPastedSet.add(column.getColId())
              }
            }
          })
          rowNodeEffected.setData(rowNodeEffected.data)
        }
      }
    })
    const pasteDoneEvent = new CustomEvent('pasteDone', {
      detail: {
        rowsPastedCount: rowsPastedSet.size,
        pastedRowData: [...rowsPastedSet].map(row => row.data),
        colPastedSet: columnPastedSet,
      },
    })
    this.document.dispatchEvent(pasteDoneEvent)
  }
  /**
   * if data is of unwanted type for column else return the refined value of data as per type
   *
   * @param dataCurrent current data to be pasted
   * @param currentColumn current column
   * @returns undefined if data is of unwanted type for column else return the refined value of data as per type
   */
  pasteBasedOnType(
    dataCurrent: string,
    currentColumn: Column,
    currentRowData: unknown,
  ) {
    const cellEditorSelector = currentColumn.getColDef().cellEditorSelector
    const cellEditorSelectorResult = cellEditorSelector
      ? cellEditorSelector({ data: currentRowData } as ICellEditorParams)
      : undefined
    const options =
      currentColumn.getColDef().cellEditorParams?.options ??
      cellEditorSelectorResult?.params.options
    const dataType =
      currentColumn.getColDef().cellEditorParams?.dataType ??
      cellEditorSelectorResult?.params.dataType
    if (dataType == 'Number') {
      dataCurrent = dataCurrent.replace(/,/g, '')
    }
    if (dataType == 'Date') {
      dataCurrent = toISOFormatFromAnyValidDateString(dataCurrent)
    }
    if (dataType == 'Select' && !Number.isNaN(Number(dataCurrent))) {
      const result = options.items.find(
        (item: { [x: string]: unknown }) => Number(dataCurrent) === item.id,
      )
      dataCurrent = result ? result.displayLabel : dataCurrent
    }
    if (
      currentColumn.getColId() == 'tranType' ||
      currentColumn.getColId() == 'cusip'
    ) {
      dataCurrent = dataCurrent.toUpperCase()
    }
    if (
      allowUpdateBasedOnType(
        dataCurrent,
        dataType ?? 'String',
        options
          ? options.bindValue
            ? options.items.map(
                (item: { [x: string]: unknown }) => item[options.bindValue],
              )
            : options.items
          : undefined,
      )
    ) {
      return dataCurrent
    }
    return undefined
  }

  getColumnIndexByColId(colId: string): number {
    const allCols = this.columnApi.getAllDisplayedColumns()
    if (allCols != null) {
      return allCols.findIndex(col => col.getColId() === colId)
    }
    return 0
  }

  setIsEdit() {
    this.isEdit = true
  }
}
